/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import {
  Button,
  FlexContainer,
  Loader,
  ModalConfirm,
  Sidebar,
  Spacer,
  Spinner,
  Text,
} from "@nordcloud/gnui";
import {
  GetAppsWithEnvironmentsDocument,
  useAddEnvToOrgUnitV2Mutation,
  useBusinessContextLazyQuery,
  useBusinessContextsQuery,
  useGetEnvironmentOrgUnitsQuery,
  useRemoveEnvFromOrgUnitV2Mutation,
} from "~/generated/graphql";
import { ErrorMessage, ListLoader, LoaderWrap, ReactTable } from "~/components";
import { dateFormat, ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { useDisclosure } from "~/hooks";
import { showError, showSuccess } from "~/services/toast";
import { flatDataToTree, generateActionConfirmText } from "~/tools";
import { BusinessContextsSelectorColumns } from "./BusinessContextsSelectorListColumns";
import { EnvironmentUnitsList } from "./EnvironmentUnitsList";
import { OrganisationUnitItem } from "./OrganisationUnitItem";
import {
  BusinessContextSelectorProps,
  EnvironmentInputProps,
  SelectedInputProps,
  SelectedOrgUnit,
  UnitSelectorProps,
} from "./types";

export function EnvironmentInput({ environmentId }: EnvironmentInputProps) {
  const [selectedOrgUnits, setSelectedOrgUnits] = React.useState<
    SelectedOrgUnit[]
  >([]);
  const [removeOrgUnit, setRemoveOrgUnit] = React.useState({
    nid: "",
    name: "",
  });
  const [isModalConfirmOrgRemoveOpen, setIsModalConfirmOrgRemoveOpen] =
    React.useState(false);
  const [envNid, setEnvNid] = React.useState("");
  const { isOpen, close, toggle } = useDisclosure();

  const { data, loading, error } = useBusinessContextsQuery({
    variables: { limit: 100, page: 0 },
  });

  const [removeEnvFromOrgUnit, { loading: isLoadingRemoveEnvFromOrgUnit }] =
    useRemoveEnvFromOrgUnitV2Mutation({
      refetchQueries: [{ query: GetAppsWithEnvironmentsDocument }],
    });

  const {
    data: envOrgUnitsV2Data,
    loading: isLoadingEnvOrgUnitsV2Data,
    refetch: envOrgUnitsV2Refetch,
  } = useGetEnvironmentOrgUnitsQuery({
    variables: { id: environmentId },
  });

  React.useEffect(() => {
    const mappedOrgUnits = (
      envOrgUnitsV2Data?.environment?.orgUnitsV2?.orgUnits ?? []
    ).map((unit) => ({
      businessContextId: unit.businessContext.id,
      unitId: unit.nid,
      unitName: unit.name,
    }));

    setEnvNid(envOrgUnitsV2Data?.environment?.nid ?? "");
    setSelectedOrgUnits(mappedOrgUnits);
  }, [envOrgUnitsV2Data]);

  const onRemoveEnvFromOrgHandler = async (
    envId: string,
    orgUnitId: string
  ) => {
    try {
      setIsModalConfirmOrgRemoveOpen(false);
      await removeEnvFromOrgUnit({ variables: { envId, orgUnitId } });
      showSuccess(SUCCESS_TEXT.environmentRemoved);
      await envOrgUnitsV2Refetch();
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const businessContexts = data?.businessContexts?.businessContexts ?? [];
  const businessContextSelectorOptions = businessContexts.map((context) => ({
    ...context,
    color: context.color ?? undefined,
  }));
  const orgUnits = envOrgUnitsV2Data?.environment?.orgUnitsV2?.orgUnits ?? [];

  return (
    <>
      {isLoadingRemoveEnvFromOrgUnit || isLoadingEnvOrgUnitsV2Data ? (
        <FlexContainer>
          <Spinner size="sm" />
          <Text weight="regular" tag="div" style={{ marginLeft: "1rem" }}>
            Loading...
          </Text>
        </FlexContainer>
      ) : (
        <EnvironmentUnitsList
          orgUnits={orgUnits}
          setIsOpen={(value) => toggle(value)}
          setIsModalConfirmOrgRemoveOpen={setIsModalConfirmOrgRemoveOpen}
          setRemoveOrgUnit={setRemoveOrgUnit}
        />
      )}
      <BusinessContextSelector
        isOpen={isOpen}
        refetchTags={envOrgUnitsV2Refetch}
        loading={loading}
        environmentId={envNid}
        selectedOrgUnits={selectedOrgUnits}
        error={error}
        options={businessContextSelectorOptions}
        onClose={close}
      />
      <ModalConfirm
        isOpen={isModalConfirmOrgRemoveOpen}
        confirm={() => onRemoveEnvFromOrgHandler(envNid, removeOrgUnit.nid)}
        actionLabel="Remove"
        contentLabel="Remove organization unit from environment"
        onClose={() => setIsModalConfirmOrgRemoveOpen(false)}
      >
        {generateActionConfirmText(removeOrgUnit.name)("delete")(
          "from environment"
        )}
      </ModalConfirm>
    </>
  );
}

function BusinessContextSelector({
  onClose,
  isOpen,
  refetchTags,
  selectedOrgUnits,
  loading,
  error,
  options,
  environmentId,
}: BusinessContextSelectorProps) {
  const [isUnitSelectorOpen, setIsUnitSelectorOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<SelectedInputProps[]>([]);

  const [
    getBusinessContext,
    {
      data: businessContextData,
      loading: isLoadingBusinessContext,
      error: businessContextError,
    },
  ] = useBusinessContextLazyQuery();

  React.useEffect(() => {
    setSelected(selectedOrgUnits);
  }, [selectedOrgUnits, refetchTags]);

  const orgUnits =
    businessContextData?.businessContext?.orgUnits?.orgUnits ?? [];

  return (
    <>
      <Sidebar
        // Css is temporary fix for issue: KLAR-1928
        // Issue occurs only on chrome (v93) on mac for browser width 2545px
        // Test scenario: expand application (on applications list) with more than 17 environments
        // Please check if it's already fixed in chrome and if so - remove this line :)
        css={{ willChange: "transform" }}
        isOpen={isOpen}
        title="Select Business Contexts"
        onClick={onClose}
      >
        <ErrorMessage error={error || businessContextError} />
        <LoaderWrap inContent loading={loading}>
          {options?.map((option) => {
            const label = selected.find((selectedItem) => {
              return selectedItem.businessContextId === option.id;
            })?.unitName;

            return (
              <OrganisationUnitItem
                key={option.id}
                item={option}
                label={label}
                onClick={() => {
                  void getBusinessContext({
                    variables: {
                      id: option.id,
                      year: dayjs().format(dateFormat.year),
                    },
                  });
                  setIsUnitSelectorOpen(!isUnitSelectorOpen);
                }}
              />
            );
          })}
        </LoaderWrap>
        <Spacer height="1rem" />
        <Button onClick={onClose}>Close</Button>
      </Sidebar>
      <OrgUnitSelector
        isUnitSelectorOpen={isUnitSelectorOpen}
        refetchTags={refetchTags}
        environmentId={environmentId}
        selected={selected}
        units={orgUnits}
        loading={isLoadingBusinessContext}
        onClose={() => {
          setIsUnitSelectorOpen(false);
        }}
      />
    </>
  );
}

function OrgUnitSelector({
  onClose,
  refetchTags,
  isUnitSelectorOpen,
  selected,
  units,
  environmentId,
  loading,
}: UnitSelectorProps) {
  const [addEnvToOrgUnit, { loading: isLoadingAddEnvToOrgUnit }] =
    useAddEnvToOrgUnitV2Mutation({
      refetchQueries: [{ query: GetAppsWithEnvironmentsDocument }],
    });

  const onUnitSelectHandler = async (unitId: string) => {
    try {
      const isSelected = selected.some((sel) => sel.unitId === unitId);
      if (isSelected) {
        showError(ERROR_TEXT.failedAddingBusinessUnit);
        return;
      }

      await addEnvToOrgUnit({
        variables: { envId: environmentId, orgUnitId: unitId },
      });
      showSuccess(SUCCESS_TEXT.environmentAdded);
      await refetchTags();
    } catch {
      showError(ERROR_TEXT.default);
    }
    onClose();
  };

  const mappedUnits = units.map((unit) => ({
    ...unit,
    parentId: unit?.nid === unit.parentNid ? null : unit.parentNid,
    subRows: [],
    rawCost: 0,
  }));

  const treeData = flatDataToTree(mappedUnits);
  const columns = BusinessContextsSelectorColumns(onUnitSelectHandler);

  return (
    <>
      {isLoadingAddEnvToOrgUnit && <Loader />}
      <Sidebar
        // Css is temporary fix for issue: KLAR-2201
        // Issue occurs only on chrome (v93) on mac for browser width 2845px
        // Test scenario: go to appliation details page, open environments tab with more than 17 environments
        // Please check if it's already fixed in chrome and if so - remove this line :)
        css={{ willChange: "transform" }}
        isOpen={isUnitSelectorOpen}
        title="Select business unit"
        onClick={onClose}
      >
        <LoaderWrap loading={loading} Component={ListLoader}>
          <ReactTable
            hover
            small
            columns={columns}
            data={treeData}
            sort={false}
          />
        </LoaderWrap>
      </Sidebar>
    </>
  );
}
